import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Modal,
  IconButton,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FactoryIcon from '@mui/icons-material/Factory';
import ConstructionIcon from '@mui/icons-material/Construction';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import MarkdownPreview from '@uiw/react-markdown-preview';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { DESIGN_TOKENS } from '../theme/designTokens';

// Mock data generation based on the provided report structure
const createMockReport = (data: {
  industry: string;
  totalObs: number;
  posObs: number;
  negObs: number;
  highRisk: number;
  medRisk: number;
  lowRisk: number;
  sif: number;
  observations: string;
  categoryCounts: string;
}) => `# EHS Risk Assessment Report - ${data.industry} Example

## Executive Summary

**Overall Safety Performance:** Based on the visual inspection of ${data.totalObs} observations, this area requires attention to address identified risks.

**Total Number of Observations:** ${data.totalObs}
- Positive Observations: ${data.posObs}
- Negative Issues: ${data.negObs}

**Risk Profile:**
- High Risk (Red): ${data.highRisk} issues
- Medium Risk (Yellow): ${data.medRisk} issues  
- Low Risk (Green): ${data.lowRisk} issues
- SIF Potential Issues: ${data.sif}

**Key Highlights:** ${data.highRisk} high-risk issue(s) and ${data.sif} SIF-potential issue(s) identified, requiring immediate action.

**Recommendations (High-Level):** Immediately address all high-risk and SIF-potential issues. Develop action plans for medium-risk issues. Reinforce positive safety practices observed.

---

${data.observations}

---

## Risk Analysis Summary

### Risk Level Distribution

| Risk Level | Count | Priority |
|------------|-------|----------|
| High Risk (Red) | ${data.highRisk} | Immediate Action Required |
| Medium Risk (Yellow) | ${data.medRisk} | Action Required Within 30 Days |
| Low Risk (Green) | ${data.lowRisk} | Monitor and Review |
| Positive Observations | ${data.posObs} | Maintain Current Practices |

### Risk Category Analysis (4E+HF Framework)

| Risk Category | Count |
|---------------|-------|
${data.categoryCounts}

### ⚠️ Serious Injury or Fatality (SIF) Potential Issues

**${data.sif} observation(s) identified with SIF potential.** These require immediate management attention and priority action planning.

---

**Risk Assessment Methodology:** This assessment uses the 4E+HF framework (Ergonomics, Energy, Exposure, Environmental, Human Factors) with quantitative risk scoring (Severity × Likelihood = Risk Score).

*This report is based on visual analysis of video frames. A comprehensive EHS assessment may require additional on-site inspection.*
`;

const mockData = [
  {
    id: 1,
    titleKey: "examples.industries.manufacturing.title",
    summaryKey: "examples.industries.manufacturing.summary",
    industry: "Manufacturing",
    icon: <FactoryIcon />,
    stats: { high: 1, medium: 2, positive: 1, sif: 1 },
    report: createMockReport({
      industry: "Manufacturing",
      totalObs: 4, posObs: 1, negObs: 3, highRisk: 1, medRisk: 2, lowRisk: 0, sif: 1,
      observations: `
### 🔴 HIGH RISK ISSUES (Immediate Action Required)

#### Issue 1: Energy
**Description:** An operator reaches into a stamping machine while it is operational to clear a jam. The machine lacks an interlock guard.
![Frame from a manufacturing video](https://images.unsplash.com/photo-1586798271654-03c1393f5454?q=80&w=800)
**Risk Assessment:**
- Severity: Catastrophic (5)
- Likelihood: Possibly occur (3)
- Risk Score: 15/25
- SIF Potential: Yes
**Recommended Action:** Immediately stop use of the machine. Install an interlock guard that stops machine operation when the access door is opened.
**Standard:** OSHA 29 CFR 1910.212

### 🟡 MEDIUM RISK ISSUES (Action Required)

#### Issue 2: Ergonomics
**Description:** An employee is observed lifting a heavy box with a bent back, placing strain on the lower back.
**Recommended Action:** Provide manual handling training focusing on proper lifting techniques.

#### Issue 3: Exposure
**Description:** A container of industrial solvent is left open, potentially exposing workers to chemical vapors.
**Recommended Action:** Ensure all chemical containers are properly sealed when not in use.

### ✅ POSITIVE OBSERVATIONS

#### Positive Practice 1: Human Factors
**Description:** All employees observed in the area are wearing the required safety glasses and steel-toed boots.
**Recommendation:** Acknowledge and reinforce the consistent use of required PPE.`,
      categoryCounts: `| Energy | 1 |
| Ergonomics | 1 |
| Exposure | 1 |
| Human Factors | 1 |`
    })
  },
  {
    id: 2,
    titleKey: "examples.industries.construction.title",
    summaryKey: "examples.industries.construction.summary",
    industry: "Construction",
    icon: <ConstructionIcon />,
    stats: { high: 2, medium: 1, positive: 1, sif: 2 },
    report: createMockReport({
      industry: "Construction",
      totalObs: 4, posObs: 1, negObs: 3, highRisk: 2, medRisk: 1, lowRisk: 0, sif: 2,
      observations: `
### 🔴 HIGH RISK ISSUES (Immediate Action Required)

#### Issue 1: Energy (Fall Hazard)
**Description:** A worker is operating near an unprotected edge on the third floor without any personal fall arrest system.
![Frame from a construction video](https://images.unsplash.com/photo-1541888946425-d81bb19240f5?q=80&w=800)
**Risk Assessment:**
- Severity: Catastrophic (5)
- Likelihood: Likely to occur (4)
- Risk Score: 20/25
- SIF Potential: Yes
**Recommended Action:** Immediately halt work in the area until a guardrail system or other fall protection is in place.
**Standard:** OSHA 29 CFR 1926.501(b)(1)

#### Issue 2: Energy (Electrical)
**Description:** An extension cord with frayed insulation is seen powering a circular saw, creating a shock hazard.
**Recommended Action:** Remove the damaged cord from service immediately and replace it.

### 🟡 MEDIUM RISK ISSUES (Action Required)

#### Issue 3: Environmental
**Description:** Construction debris and waste materials are not properly contained, creating trip hazards.
**Recommended Action:** Implement regular housekeeping procedures and provide designated waste bins.

### ✅ POSITIVE OBSERVATIONS

#### Positive Practice 1: Human Factors
**Description:** All workers observed on site are wearing hard hats.
**Recommendation:** Commend the team for consistent compliance with head protection requirements.`,
      categoryCounts: `| Energy | 2 |
| Environmental | 1 |
| Human Factors | 1 |`
    })
  },
  {
    id: 3,
    titleKey: "examples.industries.warehouse.title",
    summaryKey: "examples.industries.warehouse.summary",
    industry: "Logistics",
    icon: <WarehouseIcon />,
    stats: { high: 1, medium: 1, positive: 2, sif: 1 },
    report: createMockReport({
      industry: "Warehouse",
      totalObs: 4, posObs: 2, negObs: 2, highRisk: 1, medRisk: 1, lowRisk: 0, sif: 1,
      observations: `
### 🔴 HIGH RISK ISSUES (Immediate Action Required)

#### Issue 1: Energy (Powered Industrial Truck)
**Description:** A forklift operator is observed driving with an elevated load, obstructing their view.
![Frame from a warehouse video](https://images.unsplash.com/photo-1587293852726-70cdb120c386?q=80&w=800)
**Risk Assessment:**
- Severity: Catastrophic (5)
- Likelihood: Possibly occur (3)
- Risk Score: 15/25
- SIF Potential: Yes
**Recommended Action:** Retrain operator on safe forklift practices, including keeping loads low during travel.
**Standard:** OSHA 29 CFR 1910.178(n)(4)

### 🟡 MEDIUM RISK ISSUES (Action Required)

#### Issue 2: Ergonomics
**Description:** Boxes are stored on the top shelf of a high rack, requiring employees to reach overhead to retrieve them.
**Recommended Action:** Relocate frequently accessed items to shelves between chest and waist height.

### ✅ POSITIVE OBSERVATIONS

#### Positive Practice 1: Environmental
**Description:** Clearly marked and segregated recycling and waste bins are available and in use.
**Recommendation:** Continue to maintain excellent waste segregation practices.

#### Positive Practice 2: Human Factors
**Description:** Pedestrian walkways are clearly marked and free of obstructions.
**Recommendation:** Maintain clear separation of pedestrian and vehicle traffic.`,
      categoryCounts: `| Energy | 1 |
| Ergonomics | 1 |
| Environmental | 1 |
| Human Factors | 1 |`
    })
  }
];

const ExamplesSection = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
  paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  background: '#F9FAFB',
}));

const ReportCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  border: '1px solid #E5E7EB',
  background: '#FEFEFE',
  transition: 'all 0.3s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  '&:hover': {
    transform: 'translateY(-4px)',
    borderColor: '#4338CA',
  },
}));

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '900px',
  height: '90vh',
  backgroundColor: theme.palette.background.paper,
  border: '1px solid #E5E7EB',
  borderRadius: DESIGN_TOKENS.borderRadius.xl,
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const Examples = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedReport, setSelectedReport] = useState('');
  const { t } = useTranslation();
  
  // Set page title for analytics
  usePageTitle('Examples');

  const handleOpenModal = (report: string) => {
    setSelectedReport(report);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedReport('');
  };
  
  const currentReports = mockData;

  return (
    <ExamplesSection>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Typography variant="h1" component="h1" align="center" gutterBottom>
            {t('examples.title')}
          </Typography>
          <Typography 
            variant="h2" 
            component="h2"
            align="center" 
            paragraph
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.25rem' },
              maxWidth: '800px',
              mx: 'auto',
              mb: { xs: 6, md: 8 },
              color: 'text.secondary',
            }}
          >
            {t('examples.subtitle')}
          </Typography>
        </motion.div>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 4, mb: { xs: 8, md: 12 } }}>
          {currentReports.map((item, index) => (
            <Box key={item.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ReportCard>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    {item.icon}
                    <Typography variant="h3" component="h3" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
                      {t(item.titleKey)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3, flexGrow: 1 }}>
                    {t(item.summaryKey)}
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleOpenModal(item.report)}
                    startIcon={<VisibilityIcon />}
                  >
                    {t('examples.viewReport')}
                  </Button>
                </ReportCard>
              </motion.div>
            </Box>
          ))}
        </Box>
        
        <Modal 
          open={openModal} 
          onClose={handleCloseModal} 
          aria-labelledby="report-modal-title"
          role="dialog"
          aria-modal="true"
        >
          <ModalBox>
            <Typography id="report-modal-title" variant="h2" component="h2" sx={{ mb: 2, fontSize: '1.5rem', fontWeight: 600 }}>
              {t('examples.modalTitle')}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              <CloseIcon />
            </IconButton>
            <Box 
              component="main"
              sx={{ 
                overflowY: 'auto',
                padding: '16px 24px',
                flexGrow: 1,
                '& .wmde-markdown': {
                  background: 'transparent',
                },
                '& img': {
                  maxWidth: '100%',
                  borderRadius: '8px',
                  marginTop: '8px'
                }
              }}
              tabIndex={0}
              aria-label="Report content"
            >
              <MarkdownPreview source={selectedReport || ''} style={{ background: '#FEFEFE', color: '#111827', fontFamily: '"Inter", sans-serif' }}/>
            </Box>
            <Box component="footer" sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #E5E7EB', bgcolor: '#F9FAFB' }} role="contentinfo">
                <Button variant="outlined" onClick={handleCloseModal}>{t('examples.closePreview')}</Button>
            </Box>
          </ModalBox>
        </Modal>
        <Box sx={{ mt: 8 }}>
          <Paper sx={{ p: { xs: 3, sm: 6 }, textAlign: 'center' }}>
            <Typography variant="h2" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
              {t('examples.cta.title')}
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: '600px', mx: 'auto', mb: 4, color: 'text.secondary' }}>
              {t('examples.cta.description')}
            </Typography>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="contained" 
                size="large"
                component={RouterLink}
                to="/upload"
              >
                {t('examples.cta.button')}
              </Button>
            </motion.div>
          </Paper>
        </Box>
      </Container>
    </ExamplesSection>
  );
};

export default Examples; 